import { useMutation, useMutationState } from '@tanstack/react-query';

import type { DBBackupData } from '@sb/feathers-types';
import type { ToastState } from '@sb/ui/components';
import { CLOUD_BACKUP_TIMEOUT_MS, createBackup } from '@sbrc/services';

import useToast from './useToast';

/** Name shown when a backup carries no label, matching the Marvin backups list. */
export const UNLABELLED_BACKUP_NAME = 'Automatic backup';

/**
 * Cloud backups live under the robot's own prefix in the bucket, so neither
 * the USB volume path nor the USB folder's robot id applies here.
 * feathers-bot names the backup with the box's own cloud identity, the one
 * api-srv authenticates the upload against.
 */
const NO_USB_PATH = '';
const NO_USB_ROBOT_ID = '';

/**
 * Stable key so an in-flight capture can be observed from any component
 * instance. The mutation's own `isPending` lives on the observer, which dies
 * with the component that created it — and Settings unmounts GeneralRobotTab
 * on every sub-tab switch (SettingsGeneralContent renders it behind
 * `activeTab === 'robot'`). Without a shared key, returning to the Robot tab
 * mid-capture mounts a fresh mutation reporting `isPending: false`, re-arming
 * the button for a second concurrent backup.
 */
export const CLOUD_BACKUP_MUTATION_KEY = ['cloudBackup'] as const;

export interface CreateCloudBackupArgs {
  label?: string;
}

/**
 * How a finished capture should be reported. The service reports a capture
 * failure as a resolved `DBBackupData` with `status: 'failed'` rather than by
 * rejecting, so a resolved backup still has to be inspected for failure.
 */
export function describeBackupOutcome(backup: DBBackupData): ToastState {
  if (backup.status === 'failed' || backup.error) {
    return {
      kind: 'error',
      message: `Cloud backup failed: ${backup.error ?? 'unknown error'}`,
    };
  }

  const name = backup.label || UNLABELLED_BACKUP_NAME;

  // A backup whose registration failed is whole in the bucket but absent from
  // the checkpoint index, so it is not a restore point. Reporting it as a plain
  // success would tell the operator they are covered when they are not.
  if (backup.params?.registrationError) {
    return {
      kind: 'error',
      message: `Cloud backup "${name}" uploaded, but could not be registered as a restore point.`,
    };
  }

  return { kind: 'success', message: `Cloud backup "${name}" complete.` };
}

/**
 * The capture running right now, if any — observed through the mutation cache
 * rather than a component-local observer, so it survives the settings tab
 * unmounting and remounting. `label` is the name the running capture was
 * started with, which a remounted modal has no other way to know.
 */
export function useCloudBackupInFlight(): {
  isRunning: boolean;
  label?: string;
} {
  const running = useMutationState({
    filters: { mutationKey: CLOUD_BACKUP_MUTATION_KEY, status: 'pending' },
    select: (mutation) =>
      (mutation.state.variables as CreateCloudBackupArgs | undefined)?.label,
  });

  return { isRunning: running.length > 0, label: running[0] };
}

/**
 * Takes one on-demand complete backup of this robot to the cloud, and reports
 * the outcome as a toast — which `useToast` also records as a notification.
 *
 * Reporting happens inside `mutationFn`, not in `onSuccess`/`onError`. React
 * Query drops a mutation's component-level callbacks once the owning component
 * unmounts, and a capture the UI describes as running "for minutes" will often
 * outlive the settings tab — the operator switches tab or navigates away. With
 * the callbacks on the options object, that backup would finish with no toast
 * and no recorded notification, silently breaking the modal's promise that it
 * keeps running and reports back. `useToast` writes through `GlobalContext`,
 * whose provider sits above this tab, so reporting from here still lands.
 */
export function useCreateCloudBackup() {
  const { setToast } = useToast();

  return useMutation<DBBackupData, Error, CreateCloudBackupArgs>({
    mutationKey: CLOUD_BACKUP_MUTATION_KEY,
    mutationFn: async ({ label }) => {
      try {
        const backup = await createBackup(NO_USB_PATH, NO_USB_ROBOT_ID, {
          isCloudBackup: true,
          isCompleteBackup: true,
          label,
          timeoutMS: CLOUD_BACKUP_TIMEOUT_MS,
        });

        setToast(describeBackupOutcome(backup));

        return backup;
      } catch (error) {
        setToast({
          kind: 'error',
          message: `Cloud backup failed: ${(error as Error).message}`,
          error: error instanceof Error ? error : undefined,
        });

        // Rethrow so a still-mounted modal renders the failure too.
        throw error;
      }
    },
  });
}
